import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { useOrganization } from '@/contexts/OrganizationContext';
import { eventDeliverableRepository } from '../repositories/eventDeliverable.repository';
import { deliverableKeys } from '../services/deliverable.keys';
import type {
  EventDeliverable,
  CreateEventDeliverableDto,
  UpdateEventDeliverableDto,
} from '../types/deliverable.types';

export type { EventDeliverable };

export const useEventDeliverables = (eventId: string) => {
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.id || '';

  return useQuery({
    queryKey: deliverableKeys.eventDeliverables(orgId, eventId),
    queryFn: async (): Promise<EventDeliverable[]> => {
      if (!eventId || !orgId) return [];
      return eventDeliverableRepository.getEventDeliverables(orgId, eventId);
    },
    enabled: !!eventId && !!orgId,
  });
};

export const useCreateEventDeliverable = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { currentOrganization } = useOrganization();

  return useMutation({
    mutationFn: async (data: CreateEventDeliverableDto) => {
      const orgId = currentOrganization?.id;
      if (!orgId) throw new Error('No organization selected');
      return eventDeliverableRepository.createEventDeliverable(orgId, data);
    },
    onSuccess: (data) => {
      const orgId = currentOrganization?.id || '';
      queryClient.invalidateQueries({ queryKey: deliverableKeys.eventDeliverables(orgId, data.event_id) });
      queryClient.invalidateQueries({ queryKey: deliverableKeys.legacyEventDeliverables(data.event_id) });
      toast({
        title: 'Success',
        description: 'Deliverable added successfully',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to create deliverable',
        variant: 'destructive',
      });
    },
  });
};

export const useUpdateEventDeliverable = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { currentOrganization } = useOrganization();

  return useMutation({
    mutationFn: async ({
      id,
      ...data
    }: {
      id: string;
    } & UpdateEventDeliverableDto) => {
      const orgId = currentOrganization?.id;
      if (!orgId) throw new Error('No organization selected');
      return eventDeliverableRepository.updateEventDeliverable(orgId, id, data);
    },
    onSuccess: (data) => {
      const orgId = currentOrganization?.id || '';
      queryClient.invalidateQueries({ queryKey: deliverableKeys.eventDeliverables(orgId, data.event_id) });
      queryClient.invalidateQueries({ queryKey: deliverableKeys.legacyEventDeliverables(data.event_id) });
      toast({
        title: 'Success',
        description: 'Deliverable updated successfully',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to update deliverable',
        variant: 'destructive',
      });
    },
  });
};

export const useDeleteEventDeliverable = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { currentOrganization } = useOrganization();

  return useMutation({
    mutationFn: async ({
      id,
      eventId,
    }: {
      id: string;
      eventId: string;
    }) => {
      const orgId = currentOrganization?.id;
      if (!orgId) throw new Error('No organization selected');
      await eventDeliverableRepository.deleteEventDeliverable(orgId, id);
      return { id, eventId };
    },
    onSuccess: (data) => {
      const orgId = currentOrganization?.id || '';
      queryClient.invalidateQueries({ queryKey: deliverableKeys.eventDeliverables(orgId, data.eventId) });
      queryClient.invalidateQueries({ queryKey: deliverableKeys.legacyEventDeliverables(data.eventId) });
      toast({
        title: 'Success',
        description: 'Deliverable deleted successfully',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to delete deliverable',
        variant: 'destructive',
      });
    },
  });
};
